import axios from "axios";
import router from "../router";
import store from "../store/index";

function showSessionExpiredDialog() {
  sessionStorage.removeItem("access_token");
  sessionStorage.removeItem("refresh_token");
  store.commit("app/set_token", "");
  store.commit("setSessionOut", true);
}

function requiresAuth() {
  const route = router.currentRoute;
  return Boolean(
    (route.meta && route.meta.requiresAuth) ||
    (route.matched && route.matched.some(record => record.meta && record.meta.requiresAuth)),
  );
}

function isLoginOrRefresh(config) {
  return /\/(login|refresh-token)\/?(?:\?|$)/.test((config && config.url) || "");
}

function errorData(error) {
  return error && error.response ? error.response.data : error;
}

const service = axios.create({
  // withCredentials: true,
  timeout: 0, // no timeout
  headers: {
    "content-type": "application/json",
  },
});

// Use a separate client so a rejected refresh cannot trigger another refresh.
const refreshService = axios.create({ timeout: 30000 });
let refreshPromise = null;

function refreshAccessToken(refreshToken) {
  if (!refreshPromise) {
    refreshPromise = (async () => {
      const response = await refreshService.post(
        `${process.env.VUE_APP_AGENT_SERVICE}/api/v1/agent/auth/refresh-token`,
        null,
        { params: { token: refreshToken } },
      );
      const payload = response.data;
      const tokens = payload && (Array.isArray(payload.data) ? payload.data[0] : payload.data);
      const accessToken = payload && (payload.access_token || (tokens && tokens.access_token));
      if (
        !accessToken || payload.success === false ||
        (payload.status_code != null && (Number(payload.status_code) < 200 || Number(payload.status_code) >= 300))
      ) {
        throw payload || new Error("Unable to refresh the session.");
      }
      sessionStorage.setItem("access_token", accessToken);
      const nextRefreshToken = payload.refresh_token || (tokens && tokens.refresh_token);
      if (nextRefreshToken) sessionStorage.setItem("refresh_token", nextRefreshToken);
      store.commit("app/set_token", accessToken);
      return accessToken;
    })().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
}

async function retryUnauthorized(response, config, client) {
  const refreshToken = sessionStorage.getItem("refresh_token");
  if (!config || config._retry || !refreshToken) {
    showSessionExpiredDialog();
    return Promise.reject(response.data);
  }
  config._retry = true;
  config.headers = config.headers || {};
  const previousAuthorization = config.headers.Authorization || config.headers.authorization;
  const currentToken = sessionStorage.getItem("access_token");
  let accessToken;
  try {
    // A delayed 401 may belong to a token another request already refreshed.
    accessToken = currentToken && previousAuthorization && previousAuthorization !== `Bearer ${currentToken}`
      ? currentToken
      : await refreshAccessToken(refreshToken);
  } catch (error) {
    showSessionExpiredDialog();
    return Promise.reject(errorData(error));
  }
  delete config.headers.authorization;
  config.headers.Authorization = `Bearer ${accessToken}`;
  return client.request(config);
}

service.interceptors.request.use(
  (config) => {
    const authToken = sessionStorage.getItem("access_token");
    if (authToken) {
      config.headers = config.headers || {};
      config.headers["Authorization"] = "Bearer " + authToken;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  },
);
function installResponseHandler(client) {
  client.interceptors.response.use(
    (response) => {
      const data = response.data;
      if (data && Number(data.status_code) === 408 && requiresAuth()) {
        showSessionExpiredDialog();
        return Promise.reject(data);
      }
      if (response.status === 401 || (data && Number(data.status_code) === 401)) {
        // Let the login form display invalid credentials without refreshing.
        if (isLoginOrRefresh(response.config)) return response;
        if (!requiresAuth() && !sessionStorage.getItem("access_token") && !sessionStorage.getItem("refresh_token")) {
          return response;
        }
        return retryUnauthorized(response, response.config, client);
      }
      return response;
    },
    (error) => {
      const response = error.response;
      const data = response && response.data;
      if (
        response && (response.status === 401 || (data && Number(data.status_code) === 401)) &&
        !isLoginOrRefresh(error.config || response.config) &&
        (requiresAuth() || sessionStorage.getItem("access_token") || sessionStorage.getItem("refresh_token"))
      ) {
        return retryUnauthorized(response, error.config || response.config, client);
      }
      if (response && response.status === 408 && requiresAuth()) showSessionExpiredDialog();
      return Promise.reject(errorData(error));
    },
  );
}

installResponseHandler(service);
// Cover existing calls that import Axios directly, including this.$axios.
installResponseHandler(axios);

export default service;
